import { useEffect, useState } from "react";
import { Copy, MessageCircle, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useDash } from "@/components/dashboard/Layout";
import { PageHead, Card, Locked, StatusPill } from "@/components/dashboard/kit";
import { api } from "@/lib/api";
import { PACKAGES, inr } from "@/data/content";

const pkgName = (s) => PACKAGES.find((p) => p.slug === s)?.name || "—";

export const useEarnings = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/earnings").then((r) => setData(r.data)); }, []);
  return data;
};

export default function Partner() {
  const { user, approved } = useDash();
  const data = useEarnings();
  const link = `${window.location.origin}/register?ref=${user.referral_code}`;
  if (!approved) return <div data-testid="partner-page"><PageHead title="Partner" highlight="Panel" /><Locked text="Your partner link and earnings unlock after your ID is approved." /></div>;
  const s = data?.summary || {};
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Link copied"));

  return (
    <div data-testid="partner-page">
      <PageHead title="Partner" highlight="Panel" sub={`Earn ${data?.commission_percent ?? "–"}% commission on every package bought through your link, credited when the ID is approved.`} />
      <Card>
        <h2 className="font-display text-lg font-semibold">Your partner link</h2>
        <div className="flex flex-col sm:flex-row gap-3 mt-4">
          <input readOnly value={link} data-testid="referral-link-input" className="flex-1 rounded-2xl px-4 py-3 bg-[var(--bg-2)] border border-[var(--surface-border)] text-sm" />
          <button data-testid="copy-referral-btn" onClick={copy} className="btn-neon justify-center text-sm"><Copy size={15} /> Copy</button>
          <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent("Ilmora Academy join karo mere link se: " + link)}`} target="_blank" rel="noreferrer" className="btn-ghost justify-center text-sm"><MessageCircle size={15} /> WhatsApp</a>
        </div>
      </Card>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[["Today", s.today], ["Last 7 days", s.last7], ["Last 30 days", s.last30], ["All time", s.all_time]].map(([l, v]) => (
          <Card key={l} data-testid={`earning-${l.toLowerCase().replace(/\s/g, "-")}`} className="!p-5"><p className="text-xs text-[var(--muted)]">{l}</p><p className="font-display text-2xl font-bold neon-text mt-1">{inr(v || 0)}</p></Card>
        ))}
      </div>
      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-6 mt-6">
        <Card>
          <div className="flex items-center gap-3"><div className="icon-orb"><Wallet size={20} /></div><h2 className="font-display text-lg font-semibold">Payouts</h2></div>
          <div className="grid grid-cols-2 gap-3 mt-5">
            <div className="rounded-2xl border border-[var(--surface-border)] p-4"><p className="text-xs text-[var(--muted)]">Pending</p><p data-testid="payout-pending" className="font-bold text-lg mt-1">{inr(s.pending || 0)}</p></div>
            <div className="rounded-2xl border border-[var(--surface-border)] p-4"><p className="text-xs text-[var(--muted)]">Paid</p><p data-testid="payout-paid" className="font-bold text-lg mt-1">{inr(s.paid || 0)}</p></div>
          </div>
          <p className="text-xs text-[var(--muted)] mt-4">{user.upi_id || user.account_number ? "Payouts go to the details saved in your profile." : "Add your UPI or bank details in Profile & Payout to receive payouts."}</p>
        </Card>
        <Card>
          <h2 className="font-display text-lg font-semibold mb-3">Recent commissions</h2>
          {!data?.items.length ? <p className="text-sm text-[var(--muted)]">No commissions yet. Share your link to get started.</p> : (
            <div className="overflow-x-auto"><table className="w-full text-sm min-w-[460px]">
              <thead><tr className="text-left text-xs text-[var(--muted)]"><th className="pb-2">Name</th><th>Package</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>{data.items.slice(0, 8).map((r) => (
                <tr key={r.id} data-testid={`commission-row-${r.id}`} className="border-t border-[var(--surface-border)]"><td className="py-3 font-semibold">{r.name}</td><td className="text-xs">{pkgName(r.package)}</td><td className="font-bold text-[var(--pink)]">{inr(r.amount)}</td><td><StatusPill status={r.status === "pending" ? "unpaid" : r.status} /></td></tr>
              ))}</tbody>
            </table></div>
          )}
        </Card>
      </div>
    </div>
  );
}
